import type { IAgent } from '@/lib/api/agents.client';
import {
  effectivePersonalModel,
  type ChatModelInfo,
  type ModelCostCategory,
  type ModelPreference,
  type RecommendedModel,
} from '@/lib/api/providers.client';
import type { IChat, IMessage } from '@/lib/types/chat';

/**
 * Which model a chat runs on (KAN-1266): the model an opened chat restores,
 * the model a new chat starts on, and how the selection follows a new model
 * catalog. Everything but the notice is a pure decision; the store applies it.
 */

/**
 * The model a chat is currently on: the newest message that names one — the
 * turn the user sent counts, not just the answer that came back.
 *
 * Reading the last ANSWER instead was how a chat silently changed model on the
 * user (KAN-404). An answer that is still streaming does not exist in the
 * database yet, so the newest stored answer is the one before it — and a
 * fourteen-minute answer leaves a wide window in which any reload of the chat
 * resurrects the model of the turn before. The user in the incident had stopped
 * a run, switched model and sent again; a reload during that long answer put
 * the selector back on the run they had just killed, and their next turn went
 * out on the model they had rejected.
 *
 * A user turn carries the pick from the moment it is stored, which closes the
 * window: during a live run it is simply the newest entry.
 *
 * Stopped and failed answers deliberately count too. They name the model that
 * was actually running, and skipping them would break regeneration, where no
 * user turn is written at all and the answer is the only record of the pick.
 * An answer counts with the model it was requested on, not the fallback that
 * may have served it (``messageModelOf``, KAN-1403).
 */
export const resolveChatModel = (messages: readonly IMessage[]): string | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const modelId = messages[i]?.modelId;
    if (modelId) return modelId;
  }
  return null;
};

const findIn = (models: readonly ChatModelInfo[], id: string | null | undefined) =>
  id ? models.find((m) => m.id === id) : undefined;

/** A catalog as the discovery endpoint hands it out. */
export interface DiscoveredCatalog {
  models: ChatModelInfo[];
  /** What new chats start on: the user's own Standard model, else the workspace's. */
  defaultModel: string;
  /** The workspace's Standard model, when the catalog lists it (KAN-1437). */
  standardModel?: string;
}

/** The workspace's Standard model among a catalog's recommended models. */
const workspaceStandardModel = (recommended: readonly RecommendedModel[]): string | undefined =>
  recommended.find((r) => r.tier === 'medium')?.model_id;

/** The catalog as the chat store keeps it, in the shape the decisions here read. */
export const storedCatalog = (state: {
  availableModels: ChatModelInfo[];
  defaultChatModel: string;
  recommendedModels: readonly RecommendedModel[];
}): DiscoveredCatalog => ({
  models: state.availableModels,
  defaultModel: state.defaultChatModel,
  standardModel: workspaceStandardModel(state.recommendedModels),
});

/**
 * The model a new chat with an agent starts on, or null to start on the
 * default. A pinned agent starts on its pin. A tier-bound one on the user's
 * own model for that tier, like the model picker offers it (KAN-1921), as
 * long as the agent's model list admits it; else on the tier's model in
 * ``recommended`` (the tenant override on top of the YAML default).
 */
export const agentStartingModel = (
  agent: Pick<IAgent, 'defaultModel' | 'defaultModelTier' | 'allowedModels'>,
  recommended: readonly RecommendedModel[],
  modelPreference: ModelPreference | null
): string | null => {
  if (agent.defaultModel) return agent.defaultModel;
  const tier = agent.defaultModelTier;
  if (!tier) return null;
  const personal = effectivePersonalModel(modelPreference, tier);
  const admitted = !agent.allowedModels?.length || agent.allowedModels.includes(personal ?? '');
  if (personal && admitted) return personal;
  return recommended.find((r) => r.tier === tier)?.model_id ?? null;
};

const COST_RANK: Record<ModelCostCategory, number> = { '€': 1, '€€': 2, '€€€': 3, '€€€€': 4 };

// What a model costs as the catalog states it. Both figures rest on the same
// benchmark: the cost category decides, the average cost of a request breaks
// the tie within one category. A model without a figure counts as dearest.
const isCheaper = (a: ChatModelInfo, b: ChatModelInfo): boolean => {
  const rankA = a.cost_category ? COST_RANK[a.cost_category] : Infinity;
  const rankB = b.cost_category ? COST_RANK[b.cost_category] : Infinity;
  if (rankA !== rankB) return rankA < rankB;
  return (a.avg_cost_per_request ?? Infinity) < (b.avg_cost_per_request ?? Infinity);
};

// The cheapest of ``models``; of equally priced ones the first in the catalog.
const cheapestOf = (models: readonly ChatModelInfo[]): ChatModelInfo | undefined =>
  models.reduce<ChatModelInfo | undefined>(
    (best, candidate) => (!best || isCheaper(candidate, best) ? candidate : best),
    undefined
  );

/**
 * Where a selection goes when its model is gone or locked (KAN-1748): the
 * model new chats start on, else the workspace's Standard model, else the
 * cheapest model the user may use. Never simply the first of the catalog: it
 * is sorted for the selector, and in a typical workspace that is an expensive
 * Premium model. Only a model the user can send with right now qualifies: not
 * locked for a trial, and in low-budget mode one the selector leaves open.
 */
export const usableFallback = (
  catalog: DiscoveredCatalog,
  lowBudget: boolean
): ChatModelInfo | undefined => {
  const usable = (model: ChatModelInfo | undefined): model is ChatModelInfo =>
    !!model && !model.trial_locked && (!lowBudget || !!model.low_budget_allowed);
  const preferred = [catalog.defaultModel, catalog.standardModel]
    .map((id) => findIn(catalog.models, id))
    .find(usable);
  return preferred ?? cheapestOf(catalog.models.filter(usable));
};

/**
 * The model a selection falls back to, alike for the catalog refresh, a new
 * chat and the low-budget swap: ``usableFallback``, and where low-budget mode
 * leaves no model open, the same order without the budget (the selector then
 * shows that model locked). An empty catalog cannot tell what is gone and
 * leaves the default as it is. Empty when no model qualifies.
 */
export const fallbackModel = (catalog: DiscoveredCatalog, lowBudget: boolean): string => {
  if (catalog.models.length === 0) return catalog.defaultModel;
  return (usableFallback(catalog, lowBudget) ?? usableFallback(catalog, false))?.id ?? '';
};

/**
 * The model an opened chat continues on, and the replacement it was moved to
 * when its own model is gone from the catalog (e.g. after a model upgrade):
 * the fallback of every other selection (``fallbackModel``), the default, else
 * the Standard model, else the cheapest model the user may use (KAN-1748).
 * Never the selection of the chat open before: that is another chat's pick,
 * and it may be the most expensive model there is. None when no model
 * qualifies (the chat then keeps the current selection without a notice). An
 * empty catalog proves nothing is gone, so the chat keeps its model.
 */
export const effectiveChatModel = (
  messages: readonly IMessage[],
  catalog: DiscoveredCatalog,
  lowBudget: boolean
): { model: string | null; swappedTo: string | null } => {
  const model = resolveChatModel(messages);
  if (!model || catalog.models.length === 0 || findIn(catalog.models, model)) {
    return { model, swappedTo: null };
  }
  const replacement = fallbackModel(catalog, lowBudget) || null;
  return { model: replacement, swappedTo: replacement };
};

/** What the selection decision reads off the store. */
interface SelectionState {
  selectedModel: string;
  modelSelectedAt: number;
  currentChat: IChat | null;
}

/** What the selection decision reads off the world around the store. */
interface DiscoveryContext {
  now: number;
  lowBudget: boolean;
  /** Whether the chat with this id is the page on screen. */
  isOnScreen: (chatId: string) => boolean;
}

/** The selection to set, if any, and the notice to give about it. */
export interface SelectionChange {
  selectedModel?: string;
  announce?: { chatId: string; modelName: string };
}

// A manual pick holds this long against the low-budget swap, so a freshly
// chosen model stays put when the budget snaps back to ok mid-session.
const MODEL_GRACE_PERIOD_MS = 60_000;

// Low-budget mode: a selection the selector would lock snaps to one that
// stays open, unless it was picked within the grace period.
const needsBudgetSwap = (
  state: SelectionState,
  selected: ChatModelInfo,
  context: DiscoveryContext
): boolean => {
  const recentlyChanged =
    state.modelSelectedAt > 0 && context.now - state.modelSelectedAt < MODEL_GRACE_PERIOD_MS;
  return context.lowBudget && !recentlyChanged && !selected.low_budget_allowed;
};

// An existing chat is on its own model, and so is an agent's or guided
// preset's draft from the start.
const isOnOwnModel = (chat: IChat | null): boolean =>
  !!chat && (!chat.isDraft || !!chat.agentId || !!chat.guidedPreset);

// The selection is gone from the catalog or locked by the budget: it moves to
// the fallback (``fallbackModel``). When the open chat's model was switched off
// while that chat is on screen, sending with it would fail, so it moves on, but
// not silently. The store keeps the last chat after the user navigated away;
// only a chat that is actually shown gets the notice, opening it later brings
// it via loadChat. A low-budget swap gets no notice: its model is not gone.
const replacedSelection = (
  state: SelectionState,
  catalog: DiscoveredCatalog,
  context: DiscoveryContext
): SelectionChange => {
  const current = state.selectedModel;
  const selectedModel = fallbackModel(catalog, context.lowBudget) || current;
  const chat = state.currentChat;
  const modelGone = !!current && !findIn(catalog.models, current);
  const chatOnScreen = !!chat && !chat.isDraft && context.isOnScreen(chat.id);
  if (!chat || !chatOnScreen || !modelGone || selectedModel === current) return { selectedModel };
  const modelName = findIn(catalog.models, selectedModel)?.name ?? selectedModel;
  return { selectedModel, announce: { chatId: chat.id, modelName } };
};

/**
 * How the selection follows a new catalog: where nothing the user did speaks
 * against it.
 *
 * A selection the catalog lacks, or that low-budget mode locks, is replaced
 * (``replacedSelection``). A still-valid selection is normally left untouched.
 * But if it is NOT backed by a manual pick in this chat and no existing chat
 * is dictating it, the selection follows the tenant default: otherwise a
 * transient /models response that carried the system fallback (e.g. right
 * after login before the tenant context is resolved, or a stale value) latches
 * onto a valid-but-wrong model and never self-heals once the correct default
 * arrives. The same path moves an untouched draft onto a personal model set or
 * changed in the settings. Low-budget mode is deliberately left to the budget
 * swap.
 */
export const discoverySelection = (
  state: SelectionState,
  catalog: DiscoveredCatalog,
  context: DiscoveryContext
): SelectionChange => {
  const selected = findIn(catalog.models, state.selectedModel);
  if (!selected || needsBudgetSwap(state, selected, context)) {
    return replacedSelection(state, catalog, context);
  }
  const hasManualPick = state.modelSelectedAt > 0;
  if (hasManualPick || isOnOwnModel(state.currentChat) || context.lowBudget) return {};
  const tenantDefault = catalog.defaultModel;
  const follows = !!findIn(catalog.models, tenantDefault) && tenantDefault !== state.selectedModel;
  return follows ? { selectedModel: tenantDefault } : {};
};

/**
 * Tell the user their chat continues on another model because the one it used
 * is gone: removed from the catalog, or switched off by a model manager while
 * the chat was open (KAN-1109). Keyed per chat, so opening the chat and the
 * realtime refetch never show it twice. Runs outside React, so it builds its
 * own translator.
 */
export async function announceModelFallback(chatId: string, modelName: string): Promise<void> {
  try {
    const [{ toast }, { createTranslator }, { resolveInitialLocale }, { loadMessages }] =
      await Promise.all([
        import('sonner'),
        import('use-intl'),
        import('@/lib/i18n/resolve-locale'),
        import('@/lib/i18n/messages'),
      ]);
    const locale = resolveInitialLocale();
    const messages = (await loadMessages(locale)) as {
      chat: { modelSelector: Record<string, string> };
    };
    const t = createTranslator({ locale, messages, namespace: 'chat.modelSelector' });
    toast.info(t('modelUnavailable', { model: modelName }), { id: `model-fallback-${chatId}` });
  } catch (error) {
    console.warn('Model fallback could not be announced', error);
  }
}
